/**
 * Restoration mirror for the checkout address selection.
 *
 * Runtime source of truth is Redux `checkoutAddress.selectedAddressId`; this module only
 * lets that id survive a new document within the same tab session (reload, PWA restore,
 * chunk-recovery reload, same-tab redirects) — the same lifetime as the refresh token.
 *
 * Key is restaurant-scoped (restaurant id only, never slug/hostname), mirroring the cart's
 * `gmf-cart-v1:{restaurantId}`; the value carries the owning customer id so a restore
 * requires both scopes to match. Identifiers only — never address text or contact data.
 * Every failure is silent: a corrupted entry can only mean "nothing to restore".
 */

const CHECKOUT_ADDRESS_SESSION_PREFIX = "rx-checkout-address-v1:"

export type CheckoutAddressSessionEntry = {
  userId: string
  addressId: string
}

function canUseSessionStorage(): boolean {
  try {
    return typeof sessionStorage !== "undefined"
  } catch {
    return false
  }
}

function normalizeId(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

export function checkoutAddressSessionKey(restaurantId: string): string | null {
  const rid = normalizeId(restaurantId)
  return rid ? `${CHECKOUT_ADDRESS_SESSION_PREFIX}${rid}` : null
}

/** Stored entry for `restaurantId`, or null when absent, malformed, or storage is unavailable. */
export function readCheckoutAddressSession(restaurantId: string): CheckoutAddressSessionEntry | null {
  if (!canUseSessionStorage()) return null
  const key = checkoutAddressSessionKey(restaurantId)
  if (!key) return null
  try {
    const raw = sessionStorage.getItem(key)
    if (typeof raw !== "string" || !raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== "object") {
      sessionStorage.removeItem(key)
      return null
    }
    const userId = normalizeId((parsed as { userId?: unknown }).userId)
    const addressId = normalizeId((parsed as { addressId?: unknown }).addressId)
    if (!userId || !addressId) {
      sessionStorage.removeItem(key)
      return null
    }
    return { userId, addressId }
  } catch {
    return null
  }
}

export function writeCheckoutAddressSession(
  restaurantId: string,
  entry: CheckoutAddressSessionEntry
): void {
  if (!canUseSessionStorage()) return
  const key = checkoutAddressSessionKey(restaurantId)
  const userId = normalizeId(entry.userId)
  const addressId = normalizeId(entry.addressId)
  if (!key || !userId || !addressId) return
  try {
    sessionStorage.setItem(key, JSON.stringify({ userId, addressId }))
  } catch {
    /* private mode / quota — restoration is best-effort */
  }
}

export function clearCheckoutAddressSession(restaurantId: string): void {
  if (!canUseSessionStorage()) return
  const key = checkoutAddressSessionKey(restaurantId)
  if (!key) return
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/** Logout / session invalidation: drop every restaurant's entry. */
export function clearAllCheckoutAddressSessions(): void {
  if (!canUseSessionStorage()) return
  try {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(CHECKOUT_ADDRESS_SESSION_PREFIX)) keys.push(key)
    }
    for (const key of keys) sessionStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/**
 * Address deletion: drop the mirror entry of every restaurant that points at `addressId`.
 * Restaurant-wide rather than current-restaurant-only because the customer may have picked
 * the same address at more than one storefront in this tab session, and a deleted id must
 * never be restorable anywhere. Other restaurants' entries for other addresses are kept.
 *
 * Returns the restaurant-scoped keys that were removed (diagnostics and tests).
 */
export function clearCheckoutAddressSessionsForAddress(addressId: string): string[] {
  if (!canUseSessionStorage()) return []
  const target = normalizeId(addressId)
  if (!target) return []
  try {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (!key || !key.startsWith(CHECKOUT_ADDRESS_SESSION_PREFIX)) continue
      const raw = sessionStorage.getItem(key)
      if (typeof raw !== "string" || !raw) continue
      try {
        const parsed = JSON.parse(raw) as { addressId?: unknown }
        if (normalizeId(parsed?.addressId) === target) keys.push(key)
      } catch {
        /* corrupted entry: left to readCheckoutAddressSession to discard */
      }
    }
    for (const key of keys) sessionStorage.removeItem(key)
    return keys
  } catch {
    return []
  }
}
